import { useEffect, useState } from "react";

const LABEL = { live: "LIVE", quiet: "QUIET", stale: "STALE", polled: "POLLED", connecting: "CONNECTING", unavailable: "UNAVAILABLE" };
const CLS = {
  live: "text-emerald-300 border-emerald-800/80",
  quiet: "text-yellow-200 border-yellow-800/70",
  stale: "text-amber-200 border-amber-800/80",
  polled: "text-cyan-200 border-cyan-900/80",
  connecting: "text-slate-300 border-slate-700",
  unavailable: "text-rose-300 border-rose-900/80",
};
const TITLE = {
  live: "Feed connected and this asset traded within the last 60 s — the price is a current tick.",
  quiet: "Feed connected, but no trade has printed for this asset in over a minute — this is its last trade price, not a fresh tick.",
  stale: "This is the last price received, not a current quote.",
  stale_feed_down: "The real-time feed is disconnected or silent — this is the last price received, not a current quote.",
  stale_no_recent_trade: "Feed connected, but no trade has printed for this asset in over 5 minutes — treat the price as out of date.",
  stale_poll_overdue: "The CoinGecko refresh is overdue — this is the last polled price, not a current quote.",
  polled: "No supported live market pair — refreshed from CoinGecko about every 20 s.",
  connecting: "Feed connected — waiting for the first trade on this asset.",
  unavailable: "No live market pair and no price source available right now.",
};
const FEED = {
  connected: { txt: "feed connected", cls: "text-slate-400" },
  disconnected: { txt: "feed down", cls: "text-amber-300" },
  polled: { txt: "polled", cls: "text-cyan-300/80" },
};

export const ago = (at, now = Date.now()) => {
  if (!at) return "—";
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 1) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

export const stampTitle = (state, reason) => TITLE[`${state}_${reason}`] || TITLE[state] || TITLE.unavailable;

// Feed connection (optional) is shown separately from the asset's own last-update age and freshness badge:
// "CoinGecko · polled · updated 3s ago · POLLED" / "EV-IDEX feed · feed connected · updated 4m ago · QUIET".
// Re-renders once a second so the age keeps moving without any data fetch.
export const LiveStamp = ({ at, state = "unavailable", source, feed, reason, className = "", tid }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const f = feed ? FEED[feed] : null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono-ticker text-slate-500 ${className}`} title={`${stampTitle(state, reason)}${source ? ` Source: ${source}.` : ""}`} data-testid={tid} data-state={state} data-feed={feed || undefined} data-source={source || undefined}>
      {source && <span className="text-slate-400">{source}</span>}
      {f && <>{source && <span>·</span>}<span className={f.cls}>{f.txt}</span></>}
      {at && <>{(source || f) && <span>·</span>}<span>updated {ago(at, now)}</span></>}
      <span className={`rounded-full border px-1.5 leading-4 ${CLS[state] || CLS.unavailable}`}>{LABEL[state] || LABEL.unavailable}</span>
    </span>
  );
};

// Standalone feed-connection chip for tables that show the feed in its own column.
export const FeedChip = ({ feed, tid }) => {
  const f = FEED[feed] || { txt: "no feed", cls: "text-rose-300" };
  return <span className={`text-[10px] font-mono-ticker ${f.cls}`} data-testid={tid} data-feed={feed || "none"}>{f.txt}</span>;
};
